import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Trash2, Search, Package2, Layers, Rocket, AlertTriangle, Pencil, Check, ShoppingBasket } from 'lucide-react';
import { useAdminReleases, useRemoveItemsFromRelease, useDeleteRelease, usePublishRelease, useUpdateRelease, type LibraryRelease } from '@/hooks/useLibraryReleases';
import { useAdminAllMaterials, useAdminAllStratigraphies } from '@/hooks/useAdminPrivateInventory';
import EditBundleCompositionDialog from './EditBundleCompositionDialog';

export default function AdminReleaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: releases } = useAdminReleases();
  const release = (releases ?? []).find((r) => r.id === id);
  const removeItems = useRemoveItemsFromRelease();
  const publishRelease = usePublishRelease();
  const deleteRelease = useDeleteRelease();
  const updateRelease = useUpdateRelease();
  const { data: allMats } = useAdminAllMaterials();
  const { data: allStrats } = useAdminAllStratigraphies();
  const [editingName, setEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  const [search, setSearch] = useState('');
  const [matSel, setMatSel] = useState<Set<string>>(new Set());
  const [stratSel, setStratSel] = useState<Set<string>>(new Set());
  const [editBundleOpen, setEditBundleOpen] = useState(false);

  // IMPORTANTE: hook useMemo prima di qualsiasi early return per evitare
  // "Rendered more hooks than during the previous render" quando la release
  // arriva con un render in ritardo.
  const itemsHereMats = useMemo(() =>
    (allMats ?? []).filter((m) => release && m.release_slugs.includes(release.slug)),
    [allMats, release]
  );
  const itemsHereStrats = useMemo(() =>
    (allStrats ?? []).filter((s) => release && s.release_slugs.includes(release.slug)),
    [allStrats, release]
  );

  const filteredMats = useMemo(() => {
    if (!search) return itemsHereMats;
    const q = search.toLowerCase();
    return itemsHereMats.filter((m) => m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q));
  }, [itemsHereMats, search]);

  const filteredStrats = useMemo(() => {
    if (!search) return itemsHereStrats;
    const q = search.toLowerCase();
    return itemsHereStrats.filter((s) => s.name.toLowerCase().includes(q) || (s.system_code ?? '').toLowerCase().includes(q));
  }, [itemsHereStrats, search]);

  if (!release) return <div className="text-center py-12 text-sm text-zinc-500">Release non trovata.</div>;

  const handleRemoveMats = async () => {
    if (matSel.size === 0) return;
    if (!confirm(`Rimuovere ${matSel.size} materiali dalla release?`)) return;
    await removeItems.mutateAsync({ releaseId: release.id, itemType: 'material', itemIds: [...matSel] });
    setMatSel(new Set());
  };
  const handleRemoveStrats = async () => {
    if (stratSel.size === 0) return;
    if (!confirm(`Rimuovere ${stratSel.size} stratigrafie dalla release?`)) return;
    await removeItems.mutateAsync({ releaseId: release.id, itemType: 'stratigraphy', itemIds: [...stratSel] });
    setStratSel(new Set());
  };

  const handlePublish = async () => {
    if (release.materials_count + release.stratigraphies_count === 0) {
      if (!confirm('Pubblicare una release VUOTA? Sarà visibile nello Store ma senza contenuti.')) return;
    } else {
      if (!confirm(`Pubblicare "${release.name}" nello Store?\n\n${release.materials_count} materiali + ${release.stratigraphies_count} stratigrafie diventeranno acquistabili.`)) return;
    }
    await publishRelease.mutateAsync(release.id);
  };

  const handleDelete = async () => {
    const confirmText = prompt(
      `ATTENZIONE: questa azione cancella la release "${release.name}" e tutto il suo contenuto.\n\nScrivi esattamente il nome per confermare:`
    );
    if (confirmText !== release.name) {
      if (confirmText !== null) toast.error('Nome non corrispondente, eliminazione annullata');
      return;
    }
    try {
      await deleteRelease.mutateAsync(release.id);
      navigate('/admin/releases');
    } catch {
      // toast già mostrato dall'hook
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/releases')} className="-ml-2 mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" />Indietro
          </Button>
          {editingName ? (
            <div className="flex items-center gap-2">
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="text-xl font-bold h-10 max-w-[480px]"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateRelease.mutate({ id: release.id, name: editedName });
                    setEditingName(false);
                  } else if (e.key === 'Escape') {
                    setEditingName(false);
                  }
                }}
              />
              <Button size="sm" onClick={() => { updateRelease.mutate({ id: release.id, name: editedName }); setEditingName(false); }} disabled={updateRelease.isPending}>
                <Check className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Annulla</Button>
            </div>
          ) : (
            <h1
              className="text-2xl font-bold text-zinc-900 flex items-center gap-2 group cursor-pointer hover:text-zinc-700"
              onClick={() => { setEditedName(release.name); setEditingName(true); }}
              title="Click per rinominare"
            >
              {release.name}
              <Pencil className="h-4 w-4 text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h1>
          )}
          <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={release.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>{release.status}</Badge>
            <span>v{release.version_year}{release.version_suffix ?? ''}</span>
            <span>·</span>
            <span>{release.brand_slug}</span>
            <span>·</span>
            <span>{release.content_type}</span>
            <span>·</span>
            <span className="font-mono text-[10px]">{release.slug}</span>
            <span>·</span>
            <span>€{Number(release.price_eur).toFixed(0)}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {release.status === 'draft' && (
            <Button onClick={handlePublish} disabled={publishRelease.isPending} className="bg-emerald-600 hover:bg-emerald-700">
              {publishRelease.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />}
              Pubblica nello Store
            </Button>
          )}
          <Button onClick={handleDelete} disabled={deleteRelease.isPending} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
            {deleteRelease.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
            Elimina
          </Button>
        </div>
      </div>

      {/* ─── Rendering condizionale per content_type ──────────────────────── */}
      {release.content_type === 'bundle' ? (
        <BundleDetailSection
          bundle={release}
          allReleases={releases ?? []}
          onEditComposition={() => setEditBundleOpen(true)}
        />
      ) : (
        <>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-sm text-blue-900">
              Per <strong>aggiungere</strong> {release.content_type === 'materials' ? 'materiali' : 'stratigrafie'} a questa release, vai sul <Link to="/admin/catalogo" className="font-semibold underline">Catalogo</Link>, seleziona le righe e usa la barra azione in basso. Da qui vedi cosa c'è dentro la release e puoi rimuoverlo.
            </CardContent>
          </Card>

          <div className="relative max-w-md">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <Input placeholder="Cerca tra gli items di questa release…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>

          {release.content_type === 'stratigraphies' ? (
            <Card>
              <div className="px-3 py-2 border-b bg-zinc-50 text-xs font-semibold text-zinc-700 flex items-center gap-2">
                <Layers className="h-4 w-4 text-purple-600" />
                Stratigrafie in questa release ({itemsHereStrats.length})
              </div>
              <CardContent className="p-0">
                {stratSel.size > 0 && (
                  <div className="px-3 py-2 bg-purple-50 border-b flex items-center gap-2">
                    <span className="text-xs text-purple-900 flex-1">{stratSel.size} selezionate</span>
                    <Button size="sm" variant="destructive" onClick={handleRemoveStrats} disabled={removeItems.isPending}>
                      <Trash2 className="h-3 w-3 mr-1" />Rimuovi dalla release
                    </Button>
                  </div>
                )}
                <div className="max-h-[600px] overflow-y-auto">
                  {filteredStrats.length === 0 ? (
                    <div className="py-8 text-center text-sm text-zinc-400">Nessuna stratigrafia in questa release.</div>
                  ) : (
                    <ul className="divide-y">
                      {filteredStrats.map((s) => {
                        const sel = stratSel.has(s.id);
                        return (
                          <li key={s.id} className={`px-3 py-2 hover:bg-zinc-50 flex items-center gap-3 ${sel ? 'bg-purple-50' : ''}`}>
                            <Checkbox checked={sel} onCheckedChange={(c) => {
                              setStratSel((p) => { const n = new Set(p); if (c) n.add(s.id); else n.delete(s.id); return n; });
                            }} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-zinc-900 truncate">{s.name}</div>
                              <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                                {s.system_code && <span className="font-mono">{s.system_code}</span>}
                                {s.fire_resistance_class && <Badge variant="outline" className="text-[9px] bg-orange-50 text-orange-700 border-orange-200">{s.fire_resistance_class}</Badge>}
                                {s.acoustic_performance && <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">Rw {Math.round(s.acoustic_performance)}</Badge>}
                                {s.release_slugs.length > 1 && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">in {s.release_slugs.length} release</Badge>}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="px-3 py-2 border-b bg-zinc-50 text-xs font-semibold text-zinc-700 flex items-center gap-2">
                <Package2 className="h-4 w-4 text-blue-600" />
                Materiali in questa release ({itemsHereMats.length})
              </div>
              <CardContent className="p-0">
                {matSel.size > 0 && (
                  <div className="px-3 py-2 bg-blue-50 border-b flex items-center gap-2">
                    <span className="text-xs text-blue-900 flex-1">{matSel.size} selezionati</span>
                    <Button size="sm" variant="destructive" onClick={handleRemoveMats} disabled={removeItems.isPending}>
                      <Trash2 className="h-3 w-3 mr-1" />Rimuovi dalla release
                    </Button>
                  </div>
                )}
                <div className="max-h-[600px] overflow-y-auto">
                  {filteredMats.length === 0 ? (
                    <div className="py-8 text-center text-sm text-zinc-400">Nessun materiale in questa release.</div>
                  ) : (
                    <ul className="divide-y">
                      {filteredMats.slice(0, 500).map((m) => {
                        const sel = matSel.has(m.id);
                        return (
                          <li key={m.id} className={`px-3 py-2 hover:bg-zinc-50 flex items-center gap-3 ${sel ? 'bg-blue-50' : ''}`}>
                            <Checkbox checked={sel} onCheckedChange={(c) => {
                              setMatSel((p) => { const n = new Set(p); if (c) n.add(m.id); else n.delete(m.id); return n; });
                            }} />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-zinc-900 truncate">{m.name}</div>
                              <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                                <span className="font-mono">{m.code}</span>
                                <Badge variant="outline" className="text-[9px]">{m.category}</Badge>
                                {m.list_price && <span>€{Number(m.list_price).toFixed(2)}</span>}
                                {m.release_slugs.length > 1 && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">in {m.release_slugs.length} release</Badge>}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {filteredMats.length > 500 && (
                    <div className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-t">
                      Mostro 500 di {filteredMats.length} righe.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {release.content_type === 'bundle' && (
        <EditBundleCompositionDialog
          bundle={release}
          allReleases={releases ?? []}
          open={editBundleOpen}
          onOpenChange={setEditBundleOpen}
        />
      )}
    </div>
  );
}

// ─── Sezione dedicata ai bundle (read-only + bottone modifica composizione) ───
interface BundleDetailSectionProps {
  bundle: LibraryRelease;
  allReleases: LibraryRelease[];
  onEditComposition: () => void;
}

function BundleDetailSection({ bundle, allReleases, onEditComposition }: BundleDetailSectionProps) {
  const slugMap = useMemo(() => new Map(allReleases.map((r) => [r.slug, r])), [allReleases]);
  const composedSlugs = bundle.includes_release_slugs ?? [];
  const composedReleases = composedSlugs.map((s) => slugMap.get(s)).filter(Boolean) as LibraryRelease[];
  const orphanSlugs = composedSlugs.filter((s) => !slugMap.has(s));

  return (
    <>
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="p-3 text-sm text-emerald-900 flex items-start gap-2">
          <ShoppingBasket className="h-5 w-5 shrink-0 mt-0.5" />
          <div>
            Questo è un <strong>bundle</strong>: non contiene materiali o stratigrafie direttamente, ma raggruppa più release vendute insieme.
            Clicca <strong>Modifica composizione</strong> per scegliere quali Listino + Strat includere. Al checkout, il webhook assegna entitlement per ciascuna release inclusa.
          </div>
        </CardContent>
      </Card>

      <Card>
        <div className="px-3 py-2 border-b bg-zinc-50 text-xs font-semibold text-zinc-700 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <ShoppingBasket className="h-4 w-4 text-emerald-600" />
            Release incluse ({composedReleases.length})
          </span>
          <Button size="sm" variant="outline" onClick={onEditComposition}>
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Modifica composizione
          </Button>
        </div>
        <CardContent className="p-0">
          {composedReleases.length === 0 && orphanSlugs.length === 0 ? (
            <div className="py-8 text-center text-sm text-amber-700 bg-amber-50 flex items-center justify-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Bundle vuoto: nessuna release puntata. Cliente che paga non riceverebbe nulla.
            </div>
          ) : (
            <ul className="divide-y">
              {composedReleases.map((r) => (
                <li key={r.slug} className="px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50">
                  {r.content_type === 'materials' ? (
                    <Package2 className="h-4 w-4 text-blue-600 shrink-0" />
                  ) : r.content_type === 'stratigraphies' ? (
                    <Layers className="h-4 w-4 text-purple-600 shrink-0" />
                  ) : (
                    <ShoppingBasket className="h-4 w-4 text-emerald-600 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <Link to={`/admin/releases/${r.id}`} className="text-sm font-medium text-zinc-900 hover:underline">
                      {r.name}
                    </Link>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                      <span className="font-mono">{r.slug}</span>
                      <Badge variant="outline" className="text-[9px] bg-zinc-50">v{r.version_year}{r.version_suffix ?? ''}</Badge>
                      {r.is_current_year && <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200">Corrente</Badge>}
                      <span>{r.materials_count} mat · {r.stratigraphies_count} strat · €{Number(r.price_eur).toFixed(0)}</span>
                    </div>
                  </div>
                </li>
              ))}
              {orphanSlugs.map((s) => (
                <li key={s} className="px-3 py-2 text-xs text-red-700 bg-red-50 flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Slug puntato ma release non trovata: <span className="font-mono">{s}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}
